/**
 * Хоосон болон алдааны төлөв.
 *
 * ЗАРЧИМ: «Мэдээлэл байхгүй» гэж бичээд орхихгүй. Хоосон байгаа
 * ШАЛТГААН, дараа нь ЮУ ХИЙХ гэсэн хоёрыг үргэлж хэлнэ. Агуулахын
 * ажилтан бээлийтэй, гэрэл муутай орчинд ажилладаг — таамаглах зав
 * байхгүй.
 */

import type { ReactNode } from 'react';

type Tone = 'neutral' | 'alert';

/** Хоосон дугуй тэмдэг — SVG, гуравдагч icon сан татахгүй. */
function Glyph({ tone }: { tone: Tone }) {
  return (
    <span
      className={`flex size-10 shrink-0 items-center justify-center rounded-full ${
        tone === 'alert' ? 'bg-alert-bg text-alert-ink' : 'bg-well text-ink-3'
      }`}
      aria-hidden
    >
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        {tone === 'alert' ? (
          <path
            d="M9 5.2v4.4M9 12.4h.01M8.13 2.3 1.7 13.4A1 1 0 0 0 2.56 15h12.88a1 1 0 0 0 .86-1.6L9.87 2.3a1 1 0 0 0-1.74 0Z"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <path
            d="M2.5 5.5 9 2l6.5 3.5v7L9 16l-6.5-3.5v-7Zm0 0L9 9m0 0 6.5-3.5M9 9v7"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
    </span>
  );
}

export function EmptyState({
  title,
  description,
  action,
  tone = 'neutral',
  compact = false,
}: {
  title: string;
  /** Яагаад хоосон байгаа, эсвэл юу хийвэл дүүрэх */
  description?: string;
  /** Дараагийн алхмын товч эсвэл холбоос */
  action?: ReactNode;
  tone?: Tone;
  /** Картан доторх жижиг хэсэгт — босоо зайг багасгана */
  compact?: boolean;
}) {
  return (
    <div
      className={`flex flex-col items-center gap-3 px-4 text-center ${compact ? 'py-6' : 'py-10'}`}
    >
      <Glyph tone={tone} />
      {/* Кирилл гарчиг урт байдаг тул тайруулахгүй, өргөнөөр нь боож өгнө */}
      <span className="max-w-[42ch] text-h3 text-balance">{title}</span>
      {description ? (
        <span className="max-w-[52ch] text-meta text-ink-2 text-pretty">{description}</span>
      ) : null}
      {action ? <div className="mt-1 flex flex-wrap justify-center gap-2">{action}</div> : null}
    </div>
  );
}

/**
 * Алдааны төлөв — EmptyState-ийн alert хувилбар + дахин оролдох товч.
 * `ErrorNote` нь нэг мөрийн мэдэгдэлд, энэ нь бүтэн блок хоосон үлдэхэд.
 */
export function ErrorState({
  title = 'Өгөгдөл ачаалж чадсангүй',
  message,
  onRetry,
}: {
  title?: string;
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <EmptyState
      tone="alert"
      title={title}
      description={message}
      action={
        onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-pill bg-accent px-4 py-2 text-meta font-medium text-white
              transition hover:bg-accent-fill-hover active:translate-y-[0.5px]"
          >
            Дахин оролдох
          </button>
        ) : null
      }
    />
  );
}
